/**
 * @file DigestViewerModal Component
 * @description Reusable modal that fetches and displays a digest's executive summary
 * along with full activity detail sections (decisions, status changes, at-risk items, etc.).
 * Used by both the toast "View Digest" action and the history table row click.
 */

import { useDigest } from '@medsuite/api-client';
import { Modal } from '@medsuite/ui-components';

import type {
  DigestDecision,
  DigestStatusChange,
  DigestNewInvestment,
  DigestAtRiskItem,
  DigestDatePushout,
  DigestActiveInvestment,
  DigestComment,
} from '@medsuite/api-client';

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Format a date string for display (date + time)
 */
function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format a date string as a short display date
 */
function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Format a date string as a compact date (no time)
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return 'N/A';
  }
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Get badge color class based on count
 */
function getCountBadgeClass(count: number, isRisk: boolean = false): string {
  if (isRisk && count > 0) {
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
  }
  if (count === 0) {
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
}

/**
 * Map decision status code to display label
 */
function getDecisionLabel(status: string): string {
  const map: Record<string, string> = {
    A: 'Approved',
    approved: 'Approved',
    D: 'Deferred',
    deferred: 'Deferred',
    R: 'Rejected',
    rejected: 'Rejected',
    C: 'Cancelled',
    cancelled: 'Cancelled',
  };
  return map[status] ?? status;
}

/**
 * Map decision status code to a border color class
 */
function getDecisionColor(status: string): string {
  const s = status.toLowerCase();
  if (s === 'a' || s === 'approved') {
    return 'border-green-400 dark:border-green-600';
  }
  if (s === 'd' || s === 'deferred') {
    return 'border-amber-400 dark:border-amber-600';
  }
  if (s === 'r' || s === 'rejected') {
    return 'border-red-400 dark:border-red-600';
  }
  return 'border-gray-400 dark:border-gray-600';
}

/**
 * Replace underscores and title-case a status string
 */
function formatStatus(status: string | null | undefined): string {
  if (!status) {
    return '';
  }
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Section heading component
// ---------------------------------------------------------------------------

function SectionHeading({ title, count }: { title: string; count: number }) {
  return (
    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
      {title}
      <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
        ({count})
      </span>
    </h4>
  );
}

// ---------------------------------------------------------------------------
// Activity section renderers
// ---------------------------------------------------------------------------

function DecisionsSection({ items }: { items: DigestDecision[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <SectionHeading title="Formal Decisions" count={items.length} />
      <div className="space-y-2">
        {items.map((d) => (
          <div
            key={d.investment_decision_id}
            className={`border-l-4 ${getDecisionColor(d.investment_status)} pl-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-r`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {d.investment_title}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                {getDecisionLabel(d.investment_status)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Decision by {d.decision_maker_name} on {formatDate(d.decision_date)}
            </p>
            {d.decision_rationale && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">
                {d.decision_rationale.length > 200
                  ? `${d.decision_rationale.slice(0, 200)}...`
                  : d.decision_rationale}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusChangesSection({ items }: { items: DigestStatusChange[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <SectionHeading title="Status Changes" count={items.length} />
      <div className="space-y-2">
        {items.map((s) => (
          <div
            key={s.status_history_id}
            className="border-l-4 border-blue-400 dark:border-blue-600 pl-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-r"
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {s.investment_title}
            </span>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {formatStatus(s.previous_status) || 'New'} &rarr; {formatStatus(s.new_status)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Changed by {s.changed_by} on {formatDate(s.changed_at)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewInvestmentsSection({ items }: { items: DigestNewInvestment[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <SectionHeading title="New Investments" count={items.length} />
      <div className="space-y-2">
        {items.map((inv) => (
          <div
            key={inv.investment_id}
            className="border-l-4 border-green-400 dark:border-green-600 pl-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-r"
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {inv.title}
            </span>
            {inv.prime_objective && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {inv.prime_objective.length > 200
                  ? `${inv.prime_objective.slice(0, 200)}...`
                  : inv.prime_objective}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AtRiskSection({ items }: { items: DigestAtRiskItem[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <SectionHeading title="At-Risk Items" count={items.length} />
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.roadmap_item_id}
            className="border-l-4 border-red-400 dark:border-red-600 pl-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-r"
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {item.investment_title}
            </span>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Roadmap: {item.roadmap_title}
            </p>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                {item.risk_reason}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Target: {formatDate(item.end_date)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {item.estimated_remaining_days}d remaining
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DatePushoutsSection({ items }: { items: DigestDatePushout[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <SectionHeading title="Date Pushouts" count={items.length} />
      <div className="space-y-2">
        {items.map((p, idx) => (
          <div
            key={`${p.roadmap_item_id}-${idx}`}
            className="border-l-4 border-amber-400 dark:border-amber-600 pl-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-r"
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {p.investment_title}
            </span>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Roadmap: {p.roadmap_title}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {formatDate(p.old_value)} &rarr; {formatDate(p.new_value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivePipelineSection({ items }: { items: DigestActiveInvestment[] }) {
  if (items.length === 0) {
    return null;
  }

  // Group by status
  const groups: Record<string, DigestActiveInvestment[]> = {};
  for (const item of items) {
    const key = item.status;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
  }

  const statusOrder = ['approved', 'planned', 'in_progress'];
  const sortedKeys = Object.keys(groups).sort(
    (a, b) => (statusOrder.indexOf(a) === -1 ? 99 : statusOrder.indexOf(a)) -
              (statusOrder.indexOf(b) === -1 ? 99 : statusOrder.indexOf(b))
  );

  return (
    <div className="space-y-2">
      <SectionHeading title="Active Pipeline" count={items.length} />
      <div className="space-y-3">
        {sortedKeys.map((status) => (
          <div key={status}>
            <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              {formatStatus(status)} ({groups[status].length})
            </h5>
            <ul className="space-y-0.5 pl-3">
              {groups[status].map((inv) => (
                <li key={inv.investment_id} className="text-sm text-gray-700 dark:text-gray-300">
                  {inv.title}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function CommentsSection({ items }: { items: DigestComment[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <SectionHeading title="Recent Comments" count={items.length} />
      <div className="space-y-2">
        {items.map((c) => (
          <div
            key={c.investment_comment_id}
            className="border-l-4 border-gray-300 dark:border-gray-600 pl-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-r"
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {c.investment_title}
            </span>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {c.comment.length > 150 ? `${c.comment.slice(0, 150)}...` : c.comment}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {c.comment_by_name} &middot; {formatDate(c.comment_date)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DigestViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  digestId: number | null;
}

export function DigestViewerModal({ isOpen, onClose, digestId }: DigestViewerModalProps) {
  const { data: digest, isLoading } = useDigest(digestId ?? 0, {
    enabled: isOpen && digestId != null && digestId > 0,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        digest
          ? `Daily Digest - ${formatShortDate(digest.generated_at)}`
          : 'Daily Digest'
      }
      size="xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <svg
            className="animate-spin h-8 w-8 text-indigo-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      ) : digest ? (
        <div className="max-h-[70vh] overflow-y-auto space-y-5 pr-1">
          {/* Period info */}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Period: {formatDateTime(digest.period.start)} - {formatDateTime(digest.period.end)}
          </p>

          {/* Activity count badges */}
          {digest.counts && (
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getCountBadgeClass(digest.counts.decisions)}`}>
                {digest.counts.decisions} Decisions
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getCountBadgeClass(digest.counts.status_changes)}`}>
                {digest.counts.status_changes} Status Changes
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getCountBadgeClass(digest.counts.new_investments)}`}>
                {digest.counts.new_investments} New Investments
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getCountBadgeClass(digest.counts.comments)}`}>
                {digest.counts.comments} Comments
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getCountBadgeClass(digest.counts.active_investments)}`}>
                {digest.counts.active_investments} Active Pipeline
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getCountBadgeClass(digest.counts.at_risk_items, true)}`}>
                {digest.counts.at_risk_items} At-Risk Items
              </span>
            </div>
          )}

          {/* Executive summary text */}
          {digest.summary_text ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Executive Summary
              </h4>
              {digest.summary_text.split('\n').map((paragraph, idx) =>
                paragraph.trim() ? (
                  <p key={idx} className="text-gray-600 dark:text-gray-400 mb-2">
                    {paragraph}
                  </p>
                ) : null
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <p className="text-sm">No summary available</p>
            </div>
          )}

          {/* Activity detail sections */}
          {digest.sections && (
            <div className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-5">
              <DecisionsSection items={digest.sections.decisions} />
              <StatusChangesSection items={digest.sections.status_changes} />
              <NewInvestmentsSection items={digest.sections.new_investments} />
              <AtRiskSection items={digest.sections.at_risk_items} />
              <DatePushoutsSection items={digest.sections.date_pushouts} />
              <ActivePipelineSection items={digest.sections.active_investments} />
              <CommentsSection items={digest.sections.comments} />
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <p className="text-sm">No digest data found</p>
        </div>
      )}
    </Modal>
  );
}
